import { createReducer, on } from "@ngrx/store";
import {
  ProgramCampaigns,
  ProgramBoosts,
  CampaignLinks,
  ProgramLocations,
  ProgramState,
  initialState,
  ApiKeys,
  Customers,
  ProgramFeatures,
  ProgramIntegrations,
  Coupons,
  RewardsList,
  RewardsDetails,
  ProgramDevices,
  ProgramSections,
} from "./state";
import * as programActions from "./actions";
import { CouponBatchDto, CouponInfoDto, Program, ProgramConfig, ProgramIntegration, SectionDto, SubscriptionInfo, WalletConfig } from "../../services/api";
import { WalletConfigUpdate } from "../../interfaces/wallet-config.interface";
import { MCP_INTEGRATION_NAME, PAGINATION_SIZE } from "../../constants";
import { HttpStatusCode } from "@angular/common/http";
import { mergeWalletConfig } from "../../utils/merge/merge";

// cdk's moveItemInArray, kept here: importing @angular/cdk/drag-drop would ship all of it in the initial bundle
const moveItem = <T>(items: T[], fromIndex: number, toIndex: number): T[] => {
  const clamp = (index: number): number => Math.max(0, Math.min(items.length - 1, index));
  const moved = [...items];
  moved.splice(clamp(toIndex), 0, ...moved.splice(clamp(fromIndex), 1));
  return moved;
};

const removeEmptyPoster = (wallet: WalletConfigUpdate): WalletConfigUpdate => {
  if (!wallet.ios?.poster || Object.keys(wallet.ios.poster).length > 0) return wallet;

  const iosWithoutPoster = { ...wallet.ios };
  delete iosWithoutPoster.poster;
  return { ...wallet, ios: iosWithoutPoster };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const diffRecords = (value: Record<string, unknown>, base: Record<string, unknown>): Record<string, unknown> => {
  const diff: Record<string, unknown> = {};

  Object.entries(value).forEach(([key, nextValue]) => {
    if (nextValue === undefined) return;
    // A null override only matters when there is an inherited value to clear.
    if (nextValue === null && (base[key] === undefined || base[key] === null)) return;
    // Nor does an empty object (the front fields always travel, empty when none is configured).
    if (isRecord(nextValue) && Object.keys(nextValue).length === 0 && !isRecord(base[key])) return;

    const baseValue = base[key];
    if (isRecord(nextValue) && isRecord(baseValue)) {
      const nestedDiff = diffRecords(nextValue, baseValue);
      if (Object.keys(nestedDiff).length > 0) diff[key] = nestedDiff;
      return;
    }

    if (nextValue !== baseValue) diff[key] = nextValue;
  });

  return diff;
};

const diffWalletConfig = (wallet: WalletConfigUpdate, base: WalletConfig): WalletConfigUpdate =>
  diffRecords(wallet, base as unknown as Record<string, unknown>);

// The reward's status chip reads rewardsList: it follows its coupon's status (archived rewards keep theirs)
const withCouponStatus = (state: ProgramState, coupon: CouponInfoDto): RewardsList | null =>
  state.rewardsList && Object.values(state.rewards)
    .filter(reward => reward.couponId === coupon.id && state.rewardsList[reward.id] && !state.rewardsList[reward.id].deletedAt)
    .reduce((rewardsList, reward) => ({
      ...rewardsList,
      [reward.id]: {
        ...rewardsList[reward.id],
        status: coupon.status,
        totalCodes: coupon.totalCodes,
        claimedCodes: coupon.claimedCodes,
      },
    }), state.rewardsList);

export const programReducer = createReducer(
  initialState,

  // The shop's program is gone or replaced (a reinstall): nothing of the previous install survives, except what the
  // initializer already asked again for the shop (features and the integrations catalog)
  on(programActions.programNotFound, (state): ProgramState => ({ ...initialState, features: state.features, integrations: state.integrations })),

  on(programActions.createProgram, (state): ProgramState => ({ ...state, onboardingStatus: "creating-program" })),

  on(programActions.getProgram, (state, { loadingFullScreen }): ProgramState => {
    if (loadingFullScreen) {
      return { ...state, loadingFullScreen: true };
    } else {
      return { ...state, loading: true };
    }
  }),

  on(programActions.getProgramSuccess, (state, { program, loadingFullScreen }): ProgramState => {
    if (loadingFullScreen) {
      return { ...state, loadingFullScreen: false, program };
    } else {
      return { ...state, loading: false, program };
    }
  }),

  on(programActions.setShopifyPlan, (state, { shopifyPlus, shopifyPlanName }): ProgramState =>
    state.program ? { ...state, program: { ...state.program, shopifyPlus, shopifyPlanName } } : state,
  ),

  on(programActions.getProgramFailure, (state, { error, loadingFullScreen }): ProgramState => {
    let program = state.program;

    if (error.status === HttpStatusCode.NotFound) program = null;

    if (loadingFullScreen) return { ...state, program, loadingFullScreen: false };
    else return { ...state, program, loading: false };
  }),

  on(
    programActions.getAnalytics,
    programActions.getAnalyticsAggregate,
    (state, { loading }): ProgramState => ({ ...state, loading: true, loadingAnalytics: loading }),
  ),

  // Campaigns of every type share one map: drop the previous type's list so the new page does not read it as empty
  on(programActions.getProgramCampaigns, (state): ProgramState => ({ ...state, campaigns: null, loading: true, loadingCampaigns: true })),

  on(programActions.getProgramTiers, (state): ProgramState => ({ ...state, loading: true, loadingTiers: true })),

  on(programActions.getBoosts, (state): ProgramState => ({ ...state, boostsLoading: true })),

  on(
    programActions.persistProgram,
    programActions.getIntegrations,
    programActions.getProgramCustomers,
    programActions.getCustomerById,
    programActions.getProgramLocations,
    programActions.addLocation,
    programActions.updateLocation,
    programActions.deleteLocation,
    programActions.createCampaign,
    programActions.updateCampaign,
    programActions.deleteCampaign,
    programActions.createBoost,
    programActions.updateBoost,
    programActions.archiveBoost,
    programActions.getProgramLinks,
    programActions.getProgramSections,
    programActions.getCampaignLinks,
    programActions.getProgramApiKeys,
    programActions.getApiKey,
    programActions.generateApiKey,
    programActions.updateApiKey,
    programActions.deleteApiKey,
    programActions.sendPush,
    programActions.addIntegration,
    programActions.addProgramIntegration,
    programActions.updateProgramIntegration,
    programActions.getMcpSettings,
    programActions.revokeMcpConnection,
    programActions.deactivateMcp,
    programActions.updateProgram,
    programActions.klaviyoCallback,
    programActions.getFeatures,
    programActions.getFeaturesFailure,
    programActions.getSegments,
    programActions.getSegmentsFailure,
    programActions.addPointsToCustomer,
    programActions.removePointsToCustomer,
    programActions.getCustomerEvents,
    programActions.getCampaignById,
    programActions.checkCampaignSlug,
    programActions.getCoupons,
    programActions.generateCouponCodeBatch,
    programActions.removeUnclaimed,
    programActions.removeCouponBatch,
    programActions.searchCoupons,
    programActions.getCouponStats,
    programActions.claimAndDownloadCouponCodes,
    programActions.getProgramRewards,
    programActions.getRewardById,
    programActions.createReward,
    programActions.updateReward,
    programActions.deleteReward,
    programActions.addDevice,
    programActions.linkPosToDevice,
    programActions.unlinkPosToDevice,
    programActions.deleteDevice,
    programActions.getProgramTierNames,
    programActions.getAllowedDomains,
    programActions.saveFromOnboarding,
    programActions.getEngagements,
    programActions.updateEngagement,
    programActions.createEngagement,
    programActions.deleteEngagement,
    programActions.updateProgramTiers,
    programActions.updateProgramTier,
    programActions.disableProgramTiers,
    (state): ProgramState => ({ ...state, loading: true }),
  ),

  on(programActions.getProgramIntegrations, (state, { isLongPolling }) => ({ ...state, loading: isLongPolling ? false : true })),

  on(programActions.getProgramDevices, (state, { isLongPolling }) => ({ ...state, loading: isLongPolling ? false : true })),

  on(programActions.getProgramSubscriptionInfo, (state, { isLongPolling }) => ({
    ...state,
    loading: isLongPolling ? false : true,
    subscriptionInfoError: false,
  })),

  on(programActions.getCouponById, (state, { isLongPolling }) => ({ ...state, loading: isLongPolling ? false : true })),

  on(
    programActions.klaviyoCallback,
    programActions.klaviyoAuthorize,
    programActions.klaviyoRevoke,
    (state): ProgramState => ({ ...state, loadingKlaviyoAuth: true }),
  ),

  on(programActions.createProgramSubscription, (state): ProgramState => ({ ...state, loading: true, loadingCreateSubscription: true })),

  on(
    programActions.setLoadingCreateProgramSubscription,
    (state, { loadingCreateSubscription }): ProgramState => ({
      ...state,
      loadingCreateSubscription,
    }),
  ),

  on(programActions.cancelProgramSubscription, (state): ProgramState => ({ ...state, loadingCancelSubscription: true })),

  on(
    programActions.setLoadingCancelProgramSubscription,
    (state, { loadingCancelSubscription }): ProgramState => ({
      ...state,
      loadingCancelSubscription,
    }),
  ),

  on(programActions.cancelProgramSubscriptionFailure, (state): ProgramState => ({ ...state, loadingCancelSubscription: false })),

  on(
    programActions.setLoadingKlaviyoAuth,
    (state, { loadingKlaviyoAuth }): ProgramState => ({
      ...state,
      loadingKlaviyoAuth,
    }),
  ),

  on(
    programActions.getFeaturesSuccess,
    (state, { features }): ProgramState => ({
      ...state,
      features: features.reduce((features, feature) => {
        features[feature.id] = feature;
        return features;
      }, {} as ProgramFeatures),
    }),
  ),

  on(programActions.getSegmentsSuccess, (state, { segments }): ProgramState => ({ ...state, segments })),

  on(programActions.setCustomersSearchTerm, (state, { customersSearchTerm }): ProgramState => ({ ...state, customersSearchTerm })),

  on(programActions.setCustomersSort, (state, { sort, sortDirection }): ProgramState => ({
    ...state,
    customersSort: sort,
    customersSortDirection: sortDirection,
  })),

  on(
    programActions.setIntegrationsBySearchTerm,
    (state, { integrationsSearchTerm }): ProgramState => ({ ...state, integrationsSearchTerm }),
  ),

  on(
    programActions.resetCustomersPagination,
    (state): ProgramState => ({
      ...state,
      customersSearchTerm: "",
      customersSort: "createdAt",
      customersSortDirection: "DESC",
      customersPaginationData: {
        limit: PAGINATION_SIZE,
        page: 1,
        total: 0,
      },
    }),
  ),

  on(programActions.updateProgramSuccess, (state, { program }): ProgramState => ({ ...state, loading: false, program })),

  on(programActions.getProgramLinksSuccess, (state, { programLinks }): ProgramState => ({ ...state, programLinks, loading: false })),

  on(
    programActions.getProgramSectionsSuccess,
    programActions.persistProgramSectionsSuccess,
    (state, { programSections }): ProgramState => ({
      ...state,
      programSections: programSections.reduce((programSections, section) => {
        const tier = section.tier || "default";
        if (!programSections[tier]) programSections[tier] = [];

        programSections[tier].push(section);
        return programSections;
      }, {} as ProgramSections),
      loading: false,
    }),
  ),

  on(programActions.getProgramTiersSuccess, (state, { tiers }): ProgramState => ({ ...state, tiers, loading: false, loadingTiers: false })),

  on(programActions.updateProgramTiersSuccess, (state, { tiers, loyaltyIntegration }): ProgramState => ({
    ...state,
    tiers,
    programIntegrations: {
      ...state.programIntegrations,
      [loyaltyIntegration.id]: loyaltyIntegration,
    },
    loading: false
  })),

  on(programActions.updateProgramTierSuccess, (state, { tier }): ProgramState => ({
    ...state,
    tiers: state.tiers.map(t => t.id === tier.id ? tier : t),
    loading: false,
  })),

  on(programActions.disableProgramTiersSuccess, (state, { loyaltyIntegration }): ProgramState => ({
    ...state,
    tiers: [],
    programIntegrations: {
      ...state.programIntegrations,
      [loyaltyIntegration.id]: loyaltyIntegration,
    },
    loading: false,
  })),

  on(programActions.getProgramTierNamesSuccess, (state, { tierNames }): ProgramState => ({ ...state, tierNames, loading: false })),

  on(programActions.persistProgramSuccess, (state, { program }): ProgramState => ({ ...state, program, loading: false })),

  on(programActions.restoreProgramDraft, (state, { program, programSections }): ProgramState => ({
    ...state,
    program,
    programSections: programSections ?? state.programSections,
  })),

  on(programActions.getIntegrationsSuccess, (state, { integrations }): ProgramState => ({ ...state, integrations, loading: false })),

  on(
    programActions.getProgramIntegrationsSuccess,
    (state, { programIntegrations }): ProgramState => ({
      ...state,
      programIntegrations: programIntegrations.reduce((programIntegrations, programIntegration) => {
        programIntegrations[programIntegration.id] = programIntegration;
        return programIntegrations;
      }, {} as ProgramIntegrations),
      loading: false,
    }),
  ),

  on(
    programActions.getAnalyticsSuccess,
    (state, { analytics }): ProgramState => ({ ...state, analytics, loading: false, loadingAnalytics: false }),
  ),

  on(
    programActions.getAnalyticsAggregateSuccess,
    (state, { analyticsAggregate, analyticsAggregateType }): ProgramState => ({
      ...state,
      analyticsAggregate: {
        ...state.analyticsAggregate,
        [analyticsAggregateType]: analyticsAggregate,
      },
      loading: false,
      loadingAnalytics: false,
    }),
  ),

  on(programActions.createProgramSuccess, (state, { program }): ProgramState => ({ ...state, program, onboardingStatus: "program-created" })),
  on(programActions.createProgramFailure, (state): ProgramState => ({ ...state, onboardingStatus: "error" })),
  on(programActions.saveFromOnboarding, (state): ProgramState => ({ ...state, onboardingStatus: "configuring" })),
  on(programActions.saveFromOnboardingFailure, (state): ProgramState => ({ ...state, onboardingStatus: "error" })),

  on(
    programActions.updateWallet,
    (state, { wallet, tier }): ProgramState => {
      const clearPoster = wallet.ios?.poster === null || (!!wallet.ios?.poster && Object.keys(wallet.ios.poster).length === 0);
      const normalizedWallet = removeEmptyPoster(wallet);
      const baseWallet = state.program.configurations.wallet;
      let nextWallet = mergeWalletConfig(baseWallet, normalizedWallet as Partial<WalletConfig>);
      if (normalizedWallet.ios && Object.prototype.hasOwnProperty.call(normalizedWallet.ios, "actions")) {
        nextWallet = {
          ...nextWallet,
          ios: {
            ...nextWallet.ios,
            actions: normalizedWallet.ios.actions as WalletConfig["ios"]["actions"],
          },
        };
      }
      // The front fields travel whole (the API replaces them whole too), so a part left out goes back to the built-in one
      (["ios", "android"] as const).forEach(platform => {
        if (!normalizedWallet[platform] || !Object.prototype.hasOwnProperty.call(normalizedWallet[platform], "fields")) return;
        const { fields: _fields, ...rest } = nextWallet[platform];
        const fields = normalizedWallet[platform].fields;
        nextWallet = { ...nextWallet, [platform]: fields ? { ...rest, fields } : rest };
      });
      if (normalizedWallet.android && Object.prototype.hasOwnProperty.call(normalizedWallet.android, "actions")) {
        nextWallet = {
          ...nextWallet,
          android: {
            ...nextWallet.android,
            actions: normalizedWallet.android.actions as WalletConfig["android"]["actions"],
          },
        };
      }
      // The poster update always carries the full poster form, so a missing optional colour means it was cleared.
      const posterUpdate = normalizedWallet.ios?.poster;
      if (posterUpdate && nextWallet.ios.poster) {
        const poster = { ...nextWallet.ios.poster };
        if (!Object.prototype.hasOwnProperty.call(posterUpdate, "footerBackgroundColor")) delete poster.footerBackgroundColor;
        if (!Object.prototype.hasOwnProperty.call(posterUpdate, "labelColor")) delete poster.labelColor;
        // The poster's own texts travel whole too, like each wallet's fields
        if (Object.prototype.hasOwnProperty.call(posterUpdate, "fields")) poster.fields = posterUpdate.fields;
        nextWallet = { ...nextWallet, ios: { ...nextWallet.ios, poster } };
      }
      if (clearPoster) {
        nextWallet = {
          ...nextWallet,
          ios: { ...nextWallet.ios, poster: {} } as unknown as WalletConfig["ios"],
        };
      }
      const tierWallet = diffWalletConfig(normalizedWallet, baseWallet);

      return {
        ...state,
        program: {
          ...state.program,
          configurations: {
            ...state.program.configurations,
            wallet: !tier || tier === "default" ? nextWallet : { ...baseWallet },
            tiers: {
              ...state.program.configurations.tiers,
              ...(!tier || tier === "default" ? {} : {
                [tier]: tierWallet,
              }),
            } as typeof state.program.configurations.tiers,
          },
        },
      };
    },
  ),

  on(
    programActions.updateEmail,
    (state, { email }): ProgramState => ({
      ...state,
      program: {
        ...state.program,
        configurations: {
          ...state.program.configurations,
          email,
        },
      },
    }),
  ),

  on(
    programActions.updateWebapp,
    (state, { web }): ProgramState => ({
      ...state,
      program: {
        ...state.program,
        configurations: {
          ...state.program.configurations,
          web,
        },
      },
    }),
  ),

  on(programActions.updateWidget, (state, { widget }): ProgramState => {
    // Without a widget the key is left out, so a save never sends one (nor null or {})
    const { widget: _previous, ...configurations } = state.program.configurations;
    return {
      ...state,
      program: {
        ...state.program,
        // The API now always returns a widget (api#491 backfilled every program); a program without one is legacy
        configurations: (widget ? { ...configurations, widget } : configurations) as ProgramConfig,
      },
    };
  }),

  on(
    programActions.addTemporalProgramSection,
    (state, { tier = "default" }): ProgramState => ({
      ...state,
      programSections: {
        ...state.programSections,
        [tier]: [
          ...(state.programSections[tier] || []),
          {
            label: "",
            content: "",
            order: (state.programSections[tier] || []).length,
            id: crypto.randomUUID(),
            tier: tier === "default" ? null : tier,
          } as SectionDto,
        ],
      },
    }),
  ),

  on(programActions.updateTemporalProgramSection, (state, { value, property, id, tier = "default" }): ProgramState => ({
    ...state,
    programSections: {
      ...state.programSections,
      [tier]: (state.programSections[tier] || []).map(section => section.id === id ? { ...section, [property]: value } : section),
    },
  })),

  on(programActions.deleteTemporalProgramSection, (state, { index, tier = "default" }): ProgramState => ({
    ...state,
    programSections: {
      ...state.programSections,
      [tier]: (state.programSections[tier] || []).filter((_, i) => i !== index),
    },
  })),

  on(programActions.reorderTemporalProgramSections, (state, { previousIndex, currentIndex, tier = "default" }): ProgramState => {
    const tierSections = [...(state.programSections[tier] || [])];

    const [movedItem] = tierSections.splice(previousIndex, 1);
    tierSections.splice(currentIndex, 0, movedItem);

    // Update the order property for each section
    const updatedSections = tierSections.map((section, index) => ({
      ...section,
      order: index,
    }));

    return {
      ...state,
      programSections: {
        ...state.programSections,
        [tier]: updatedSections,
      },
    };
  }),

  on(
    programActions.getProgramCustomersSuccess,
    (state, { limit, customers }): ProgramState => ({
      ...state,
      customers: customers.data.reduce((customers, customer) => {
        customers[customer.email] = customer;
        return customers;
      }, {} as Customers),
      customersPaginationData: {
        limit,
        page: customers.page,
        total: customers.total,
      },
      loading: false,
    }),
  ),

  on(
    programActions.getCouponsSuccess,
    (state, { limit, coupons }): ProgramState => ({
      ...state,
      coupons: coupons.data.reduce((coupons, coupon) => {
        coupons[coupon.id] = coupon;
        return coupons;
      }, {} as Coupons),
      couponsPaginationData: {
        limit,
        page: coupons.page,
        total: coupons.total,
      },
      loading: false,
    }),
  ),

  on(
    programActions.getCustomerEventsSuccess,
    (state, { customerId, paginatedCustomerEvents }): ProgramState => ({
      ...state,
      customersEvents: {
        ...state.customersEvents,
        [customerId]:
          paginatedCustomerEvents.page === 1
            ? [...paginatedCustomerEvents.data]
            : [...(state.customersEvents[customerId] || []), ...paginatedCustomerEvents.data],
      },
      customersEventsPaginationData: {
        ...state.customersEventsPaginationData,
        page: paginatedCustomerEvents.page,
        total: paginatedCustomerEvents.total,
      },
      loading: false,
    }),
  ),

  on(
    programActions.getProgramCampaignsSuccess,
    (state, { campaigns }): ProgramState => ({
      ...state,
      campaigns: campaigns.reduce((campaigns, campaign) => {
        campaigns[campaign.id] = campaign;
        return campaigns;
      }, {} as ProgramCampaigns),
      loading: false,
      loadingCampaigns: false,
    }),
  ),

  on(
    programActions.getProgramLocationsSuccess,
    (state, { locations }): ProgramState => ({
      ...state,
      locations: locations.reduce((locations, location) => {
        locations[location.id] = location;
        return locations;
      }, {} as ProgramLocations),
      loading: false,
    }),
  ),

  on(
    programActions.addLocationSuccess,
    programActions.updateLocationSuccess,
    (state, { location }): ProgramState => ({
      ...state,
      locations: {
        ...state.locations,
        [location.id]: location,
      },
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.deleteLocationSuccess,
    (state, { locationId }): ProgramState => ({
      ...state,
      locations: Object.values(state.locations ?? {}).reduce((locations, location) => {
        if (location.id !== locationId) locations[location.id] = location;
        return locations;
      }, {} as ProgramLocations),
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.createCampaignSuccess,
    programActions.updateCampaignSuccess,
    (state, { campaign }): ProgramState => ({
      ...state,
      campaigns: {
        ...state.campaigns,
        [campaign.id]: campaign,
      },
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.deleteCampaignSuccess,
    (state, { campaignId }): ProgramState => ({
      ...state,
      campaigns: Object.values(state.campaigns ?? {}).reduce((campaigns, campaign) => {
        if (campaign.id !== campaignId) campaigns[campaign.id] = campaign;
        return campaigns;
      }, {} as ProgramCampaigns),
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.getBoostsSuccess,
    (state, { boosts }): ProgramState => ({
      ...state,
      boosts: boosts.reduce((acc, boost) => {
        acc[boost.id] = boost;
        return acc;
      }, {} as ProgramBoosts),
      boostsLoading: false,
    }),
  ),

  on(
    programActions.createBoostSuccess,
    programActions.updateBoostSuccess,
    (state, { boost }): ProgramState => ({
      ...state,
      boosts: {
        ...state.boosts,
        [boost.id]: boost,
      },
      formError: "",
      boostOverlapConflictId: null,
      loading: false,
    }),
  ),

  on(
    programActions.archiveBoostSuccess,
    (state, { boostId, deletedAt }): ProgramState => ({
      ...state,
      boosts: state.boosts?.[boostId] ? { ...state.boosts, [boostId]: { ...state.boosts[boostId], deletedAt } } : state.boosts,
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.createBoostFailure,
    programActions.updateBoostFailure,
    (state, { formError, conflictingBoostId }): ProgramState => ({
      ...state,
      formError,
      boostOverlapConflictId: conflictingBoostId ?? null,
      loading: false,
    }),
  ),

  on(programActions.getBoostsFailure, (state): ProgramState => ({ ...state, boosts: state.boosts ?? {}, boostsLoading: false })),

  on(
    programActions.getCampaignLinksSuccess,
    (state, { campaignLinks }): ProgramState => ({
      ...state,
      campaignLinks: campaignLinks.reduce((campaignLinks, campaignLink) => {
        campaignLinks[campaignLink.id] = campaignLink;
        return campaignLinks;
      }, {} as CampaignLinks),
      loading: false,
    }),
  ),

  on(
    programActions.getCampaignByIdSuccess,
    (state, { campaign }): ProgramState => ({
      ...state,
      campaigns: {
        ...state.campaigns,
        [campaign.id]: campaign,
      },
      loading: false,
    }),
  ),

  on(
    programActions.updateCampaignLinkSuccess,
    (state, { campaignLink }): ProgramState => ({
      ...state,
      campaignLinks: {
        ...state.campaignLinks,
        [campaignLink.id]: campaignLink,
      },
      formError: "",
      loading: false,
    }),
  ),

  on(
    programActions.getProgramApiKeysSuccess,
    (state, { apiKeys }): ProgramState => ({
      ...state,
      apiKeys: apiKeys.reduce((apiKeys, apiKey) => {
        apiKeys[apiKey.id] = apiKey;
        return apiKeys;
      }, {} as ApiKeys),
      loading: false,
    }),
  ),

  // Not added to the list: its whole value would be persisted. Its dialog shows it once; the page reloads the masked list
  on(programActions.generateApiKeySuccess, (state): ProgramState => ({ ...state, loading: false })),

  on(
    programActions.getApiKeySuccess,
    programActions.updateApiKeySuccess,
    (state, { apiKey }): ProgramState => ({ ...state, apiKeys: { ...state.apiKeys, [apiKey.id]: apiKey }, loading: false }),
  ),

  on(
    programActions.deleteApiKeySuccess,
    (state, { apiKeyId }): ProgramState => ({
      ...state,
      apiKeys: Object.values(state.apiKeys).reduce((apiKeys, apiKey) => {
        if (apiKey.id !== apiKeyId) apiKeys[apiKey.id] = apiKey;
        return apiKeys;
      }, {} as ApiKeys),
      loading: false,
    }),
  ),

  on(
    programActions.getCustomerByIdSuccess,
    (state, { customer }): ProgramState => ({
      ...state,
      customersData: {
        ...state.customersData,
        [customer.customer.id]: customer,
      },
      loading: false,
    }),
  ),

  on(
    programActions.getCouponByIdSuccess,
    (state, { coupon }): ProgramState => ({
      ...state,
      coupons: {
        ...state.coupons,
        [coupon.id]: coupon,
      },
      // A discount added after the last one was removed makes the reward available again
      rewardsList: withCouponStatus(state, coupon),
      loading: false,
    }),
  ),

  on(
    programActions.resetFormError,
    (state): ProgramState => ({
      ...state,
      formError: "",
      boostOverlapConflictId: null,
      profileFormSaveError: null,
    }),
  ),

  on(programActions.resetCampaignSlug, (state): ProgramState => ({ ...state, campaignSlug: "" })),

  on(
    programActions.sendPushSuccess,
    (state): ProgramState => ({
      ...state,
      formError: "",
      loading: false,
    }),
  ),

  on(programActions.toggleCouponsFilter, (state, { couponsFilter }): ProgramState => {
    const newArray = [...state.couponsFilters];

    const index = newArray.indexOf(couponsFilter);

    if (index === -1) newArray.push(couponsFilter);
    else newArray.splice(index, 1);

    return {
      ...state,
      couponsFilters: newArray,
    };
  }),

  on(programActions.setRewardsView, (state, { rewardsView }): ProgramState => ({
    ...state,
    rewardsView,
  })),

  on(programActions.setCouponsSearchTerm, (state, { couponsSearchTerm }): ProgramState => ({
    ...state,
    couponsSearchTerm,
  })),

  on(
    programActions.addProgramIntegrationSuccess,
    programActions.updateProgramIntegrationSuccess,
    (state, { programIntegration, integrationName }): ProgramState => ({
      ...state,
      programIntegrations: {
        ...state.programIntegrations,
        [programIntegration.id]: { ...programIntegration, integration: { name: integrationName } } as ProgramIntegration,
      },
      loading: false,
    }),
  ),

  on(programActions.getMcpSettingsSuccess, (state, { mcpSettings }): ProgramState => ({ ...state, mcpSettings, loading: false })),

  on(programActions.revokeMcpConnectionSuccess, (state, { connectionId }): ProgramState => ({
    ...state,
    mcpSettings: state.mcpSettings && {
      ...state.mcpSettings,
      connections: state.mcpSettings.connections.filter(connection => connection.id !== connectionId),
    },
    loading: false,
  })),

  // Deactivating deletes the program integration and revokes every connection
  on(programActions.deactivateMcpSuccess, (state): ProgramState => ({
    ...state,
    programIntegrations: Object.values(state.programIntegrations)
      .filter(programIntegration => programIntegration.integration?.name !== MCP_INTEGRATION_NAME)
      .reduce((programIntegrations, programIntegration) => {
        programIntegrations[programIntegration.id] = programIntegration;
        return programIntegrations;
      }, {} as ProgramIntegrations),
    mcpSettings: null,
    loading: false,
  })),

  on(
    programActions.addPointsToCustomerSuccess,
    programActions.removePointsToCustomerSuccess,
    (state, { customer }): ProgramState => ({
      ...state,
      formError: "",
      customersData: {
        ...state.customersData,
        [customer.customer.id]: customer,
      },
      loading: false,
    }),
  ),

  on(
    programActions.getProgramSubscriptionInfoSuccess,
    (state, { subscriptionInfo, programId }): ProgramState =>
      // Drop stale responses for a program that is no longer the loaded one, so a
      // late cancel-poll refresh can't overwrite the current program's billing data.
      // Still clear the loading flag the request set, so it doesn't stay stuck true.
      state.program && state.program.id !== programId
        ? { ...state, loading: false }
        : {
            ...state,
            subscriptionInfo,
            subscriptionInfoLoaded: true,
            subscriptionInfoError: false,
            // The API only answers 402 without an active plan: once there is one, the paywall lifts
            paymentRequiredReason: subscriptionInfo.status === SubscriptionInfo.StatusEnum.Active ? null : state.paymentRequiredReason,
            loading: false,
          },
  ),

  on(programActions.setPaymentRequired, (state, { reason }): ProgramState => ({ ...state, paymentRequiredReason: reason })),

  // Flag the failure so the plan page can say the usage could not be loaded, instead of
  // rendering its zeroed fallbacks as if the merchant had used nothing of the free plan.
  on(
    programActions.getProgramSubscriptionInfoFailure,
    (state): ProgramState => ({ ...state, subscriptionInfoError: true }),
  ),

  on(
    programActions.checkCampaignSlugSuccess,
    (state, { response }): ProgramState => ({
      ...state,
      campaignSlug: response.slug,
      loading: false,
    }),
  ),

  on(programActions.generateCouponCodeBatchSuccess, (state, {
    couponId, createdFrom, codesRequested, generationMode, discountType, value, expiresInDays,
  }) => ({
    ...state,
    coupons: {
      ...state.coupons,
      [couponId]: {
        ...state.coupons[couponId],
        batches: [
          {
            codesAvailable: 0,
            codesGenerated: 0,
            codesRequested,
            couponId,
            createdFrom,
            generationMode,
            discountType,
            value,
            expiresInDays,
            createdAt: new Date().toISOString(),
          } as CouponBatchDto,
          ...state.coupons[couponId].batches,
        ],
      },
    },
    loading: false,
  })),

  // Removing the coupon's last backing batch makes its reward not_available
  on(programActions.removeUnclaimedSuccess, programActions.removeCouponBatchSuccess, (state, { coupon }) => ({
    ...state,
    coupons: {
      ...state.coupons,
      [coupon.id]: coupon,
    },
    rewardsList: withCouponStatus(state, coupon),
    loading: false,
    formError: null,
  })),

  on(
    programActions.addLocationFailure,
    programActions.updateLocationFailure,
    programActions.deleteLocationFailure,
    programActions.createCampaignFailure,
    programActions.updateCampaignFailure,
    programActions.deleteCampaignFailure,
    programActions.archiveBoostFailure,
    programActions.sendPushFailure,
    programActions.addProgramIntegrationFailure,
    programActions.updateProgramIntegrationFailure,
    programActions.revokeMcpConnectionFailure,
    programActions.deactivateMcpFailure,
    programActions.addPointsToCustomerFailure,
    programActions.removePointsToCustomerFailure,
    programActions.generateCouponCodeBatchFailure,
    (state, { formError }): ProgramState => ({ ...state, formError, loading: false }),
  ),

  on(
    programActions.getAnalyticsFailure,
    programActions.getAnalyticsAggregateFailure,
    (state): ProgramState => ({ ...state, loading: false, loadingAnalytics: false }),
  ),

  on(
    programActions.searchCouponsSuccess,
    (state, { results }) => ({ ...state, couponsSearchResults: results, loading: false }),
  ),

  on(programActions.getCouponStatsSuccess, (state, { couponId, stats }) => ({
    ...state,
    couponStats: {
      ...state.couponStats,
      [couponId]: stats,
    },
    loading: false,
  })),

  on(programActions.klaviyoRevokeSuccess, (state) => ({
    ...state,
    programIntegrations: Object
      .values(state.programIntegrations)
      .filter(integration => integration.integration.name !== Program.IntegrationEnum.Klaviyo)
      .reduce((programIntegrations, integration) => {
        programIntegrations[integration.id] = integration;
        return programIntegrations;
      }, {} as ProgramIntegrations),
    loadingKlaviyoAuth: false,
  })),

  on(programActions.getProgramRewardsSuccess, (state, { rewards, details }): ProgramState => ({
    ...state,
    rewardsList: rewards.reduce((rewards, reward) => {
      rewards[reward.id] = reward;
      return rewards;
    }, {} as RewardsList),
    rewardsDetails: details.reduce((rewardsDetails, reward) => {
      rewardsDetails[reward.id] = reward;
      return rewardsDetails;
    }, {} as RewardsDetails),
    loading: false,
  })),

  on(programActions.getRewardByIdSuccess, (state, { reward }): ProgramState => ({
    ...state,
    rewards: {
      ...state.rewards,
      [reward.id]: reward,
    },
    loading: false,
  })),

  on(programActions.createRewardSuccess, programActions.updateRewardSuccess, (state, { reward }): ProgramState => ({
    ...state,
    rewards: {
      ...state.rewards,
      [reward.id]: reward,
    },
    loading: false,
  })),

  on(programActions.createRewardFailure, programActions.updateRewardFailure, (state, { formError }): ProgramState => ({
    ...state,
    formError,
    loading: false,
  })),

  on(programActions.deleteRewardSuccess, (state, { rewardId }): ProgramState => ({
    ...state,
    rewards: {
      ...state.rewards,
      [rewardId]: undefined,
    },
  })),

  on(programActions.reorderProgramRewards, (state, { currentIndex, previousIndex }): ProgramState => {
    const rewardsList = moveItem(Object.values(state.rewardsList ?? {}), previousIndex, currentIndex);

    return {
      ...state,
      rewardsList: rewardsList.reduce((rewards, reward, index) => {
        rewards[reward.id] = { ...reward, order: index };
        return rewards;
      }, {}),
    };
  }),

  on(programActions.getProgramDevicesSuccess, (state, { devices }): ProgramState => ({
    ...state,
    devices: devices.reduce((devices, device) => {
      devices[device.id] = device;
      return devices;
    }, {} as ProgramDevices),
    loading: false,
  })),

  on(programActions.addDeviceSuccess, (state, { device }): ProgramState => ({
    ...state,
    devices: {
      ...state.devices,
      [device.id]: device,
    },
    loading: false,
  })),

  on(programActions.linkPosToDeviceSuccess, programActions.unlinkPosToDeviceSuccess, (state, { device }): ProgramState => ({
    ...state,
    devices: {
      ...state.devices,
      [device.id]: device,
    },
    loading: false,
  })),

  on(programActions.deleteDeviceSuccess, (state, { deviceId }): ProgramState => ({
    ...state,
    devices: Object
      .values(state.devices)
      .filter(device => device.id !== deviceId).reduce((devices, device) => ({
        ...devices,
        [device.id]: device,
      }), {} as ProgramDevices),
    loading: false,
  })),

  on(programActions.getAllowedDomainsSuccess, (state, { allowedDomains }): ProgramState => ({
    ...state,
    loading: false,
    allowedDomains: allowedDomains.reduce((allowedDomains, allowedDomain) => {
      allowedDomains[allowedDomain.id] = allowedDomain;
      return allowedDomains;
    }, {}),
  })),

  on(programActions.validateDomain, programActions.resetAvailableDomain, (state): ProgramState => ({ ...state, availableDomain: null })),

  on(programActions.validateDomainSuccess, (state, { availableDomain }): ProgramState => ({
    ...state,
    availableDomain,
  })),

  on(programActions.validateDomainFailure, (state): ProgramState => ({
    ...state,
    availableDomain: false,
  })),

  on(
    programActions.addDomain,
    programActions.editDomain,
    programActions.deleteDomain,
    (state): ProgramState => ({ ...state, loading: true }),
  ),

  on(
    programActions.editDomainFailure,
    programActions.addDomainFailure,
    programActions.deleteDomainFailure,
    (state, { error }): ProgramState => ({ ...state, formError: error, loading: false }),
  ),

  on(programActions.addDomainSuccess, (state, { domain }): ProgramState => ({
    ...state,
    allowedDomains: {
      ...state.allowedDomains,
      [domain.id]: domain,
    },
    loading: false,
  })),

  on(programActions.editDomainSuccess, (state, { domain }): ProgramState => ({
    ...state,
    allowedDomains: {
      ...state.allowedDomains,
      [domain.id]: domain,
    },
    loading: false,
  })),

  on(programActions.deleteDomainSuccess, (state, { id }): ProgramState => ({
    ...state,
    allowedDomains: Object.values(state.allowedDomains)
      .filter(allowedDomain => allowedDomain.id !== id)
      .reduce((allowedDomains, allowedDomain) => {
        allowedDomains[allowedDomain.id] = allowedDomain;
        return allowedDomains;
      }, {}),
    loading: false,
  })),

  on(programActions.updateRegisterFields, (state, { registerFields }): ProgramState => ({
    ...state,
    program: {
      ...state.program,
      registerFields,
    },
  })),

  on(programActions.saveFromOnboardingSuccess, (state, {
    rewards,
    coupons,
    updatedProgram,
    allowedDomain,
  }): ProgramState => ({
    ...state,
    program: updatedProgram ? { ...state.program, ...updatedProgram } : state.program,
    rewards: {
      ...state.rewards,
      ...rewards.reduce((rewards, reward) => ({ ...rewards, [reward.id]: reward }), {}),
    },
    coupons: {
      ...state.coupons,
      ...coupons.reduce((coupons, coupon) => ({ ...coupons, [coupon.id]: coupon }), {}),
    },
    allowedDomains: allowedDomain ? {
      ...state.allowedDomains,
      [allowedDomain.id]: allowedDomain,
    } : state.allowedDomains,
    loading: false,
    onboardingStatus: "done",
  })),

  on(programActions.getEngagementsSuccess, (state, { engagements }): ProgramState => ({
    ...state,
    engagements: engagements.reduce((engagements, engagement) => {
      engagements[engagement.name] = engagement;
      return engagements;
    }, {}),
    loading: false,
  })),

  on(
    programActions.updateEngagementSuccess,
    programActions.createEngagementSuccess,
    (state, { programEngagementFlow }): ProgramState => {
      const engagementFlow = Object.values(state.engagements ?? {})
        .find(engagement => engagement.id === programEngagementFlow.engagementFlowId);
      const previous = engagementFlow.programEngagementFlows[0];

      return {
        ...state,
        engagements: {
          ...state.engagements,
          [engagementFlow.name]: {
            ...engagementFlow,
            programEngagementFlows: [{
              ...programEngagementFlow,
              title: previous?.title ?? "",
              description: previous?.description ?? "",
              longDescription: previous?.longDescription ?? "",
            }],
          },
        },
        loading: false,
      };
    },
  ),

  on(
    programActions.deleteEngagementSuccess,
    (state, { engagementFlowId }): ProgramState => {
      const engagementFlow = Object.values(state.engagements ?? {})
        .find(engagement => engagement.id === engagementFlowId);
      if (!engagementFlow) return { ...state, loading: false };
      return {
        ...state,
        engagements: {
          ...state.engagements,
          [engagementFlow.name]: {
            ...engagementFlow,
            programEngagementFlows: [],
          },
        },
        loading: false,
      };
    },
  ),

  on(programActions.updateProgramTiersFailure, (state, { reason }): ProgramState => ({ ...state, loading: false, formError: reason })),

  on(
    programActions.klaviyoAuthorizeFailure,
    programActions.klaviyoCallbackFailure,
    programActions.klaviyoCallbackSuccess,
    programActions.klaviyoRevokeFailure,
    state => ({ ...state, loadingKlaviyoAuth: false }),
  ),

  on(
    programActions.createProgramSubscriptionFailure,
    programActions.createProgramSubscriptionSuccess,
    (state): ProgramState => ({ ...state, loading: false, loadingCreateSubscription: false }),
  ),

  on(
    programActions.persistProgramFailure,
    (state, { reason }): ProgramState => ({ ...state, loading: false, formError: reason }),
  ),

  on(
    programActions.getIntegrationsFailure,
    programActions.getCustomerByIdFailure,
    programActions.getProgramLinksFailure,
    programActions.getCampaignLinksFailure,
    programActions.getProgramApiKeysFailure,
    programActions.getProgramIntegrationsFailure,
    programActions.getMcpSettingsFailure,
    programActions.addIntegrationFailure,
    programActions.addIntegrationSuccess,
    programActions.updateProgramFailure,
    programActions.klaviyoAuthorizeSuccess,
    programActions.klaviyoAuthorizeFailure,
    programActions.klaviyoCallbackSuccess,
    programActions.klaviyoCallbackFailure,
    programActions.getCustomerEventsFailure,
    programActions.getProgramSubscriptionInfoFailure,
    programActions.getCampaignByIdFailure,
    programActions.checkCampaignSlugFailure,
    programActions.getCouponsFailure,
    programActions.getCouponByIdFailure,
    programActions.searchCouponsFailure,
    programActions.getCouponStatsFailure,
    programActions.claimAndDownloadCouponCodesSuccess,
    programActions.claimAndDownloadCouponCodesFailure,
    programActions.getRewardByIdFailure,
    programActions.deleteRewardFailure,
    programActions.getProgramDevicesFailure,
    programActions.addDeviceFailure,
    programActions.linkPosToDeviceFailure,
    programActions.unlinkPosToDeviceFailure,
    programActions.deleteDeviceFailure,
    programActions.getProgramTierNamesFailure,
    programActions.updateProgramTiersFailure,
    programActions.disableProgramTiersFailure,
    programActions.getAllowedDomainsFailure,
    programActions.saveFromOnboardingFailure,
    (state): ProgramState => ({ ...state, loading: false }),
  ),

  on(programActions.removeUnclaimedFailure, programActions.removeCouponBatchFailure,
    (state, { formError }): ProgramState => ({ ...state, loading: false, formError })),
  on(programActions.updateProgramFailure, programActions.createProgramSubscriptionFailure, programActions.claimAndDownloadCouponCodesFailure,
    (state, { reason }): ProgramState => ({ ...state, loading: false, loadingCreateSubscription: false, formError: reason })),
  on(programActions.assignCouponFailure, programActions.sendGiftCardFailure, programActions.sendLoyaltyCardFailure,
    programActions.addDeviceFailure, programActions.generateApiKeyFailure, programActions.getApiKeyFailure, programActions.updateApiKeyFailure, programActions.deleteApiKeyFailure,
    programActions.upsertProgramTranslationsFailure, programActions.upsertSectionTranslationsFailure,
    (state, { error }): ProgramState => ({ ...state, loading: false, formError: error })),
  on(programActions.createProgramFailure, programActions.saveFromOnboardingFailure,
    (state, { error }): ProgramState => ({ ...state, loading: false, formError: error })),
  on(programActions.assignCoupon, programActions.sendGiftCard, programActions.sendLoyaltyCard,
    programActions.persistProgram, programActions.updateProgram, programActions.addDevice, programActions.generateApiKey,
    programActions.getApiKey, programActions.updateApiKey, programActions.deleteApiKey,
    programActions.createProgramSubscription, programActions.claimAndDownloadCouponCodes,
    programActions.createProgram, programActions.saveFromOnboarding, programActions.addLocation,
    programActions.upsertProgramTranslations, programActions.upsertSectionTranslations,
    (state): ProgramState => ({ ...state, formError: "" })),

  // Engagement and campaign editors show these in an inline banner, so a failed save is never silent (BFS 4.2.4)
  on(
    programActions.updateEngagementFailure,
    programActions.createEngagementFailure,
    programActions.deleteEngagementFailure,
    programActions.upsertEarningFlowTranslationsFailure,
    programActions.upsertCampaignTranslationsFailure,
    (state, { error }): ProgramState => ({ ...state, formError: error, loading: false }),
  ),

  // Same for the loyalty editors (rewards, tiers, coupon campaigns)
  on(
    programActions.upsertRewardTranslationsFailure,
    programActions.upsertTierTranslationsFailure,
    programActions.upsertCampaignTranslationsFailure,
    (state, { error }): ProgramState => ({ ...state, formError: error, loading: false }),
  ),
  on(programActions.updateProgramTierFailure, (state, { reason }): ProgramState => ({ ...state, formError: reason, loading: false })),

  // A failed first fetch ends as an empty list (with the error toast), never as an endless spinner
  on(programActions.getProgramCustomersFailure, (state): ProgramState => ({ ...state, customers: state.customers ?? {}, loading: false })),
  on(programActions.getProgramLocationsFailure, (state): ProgramState => ({ ...state, locations: state.locations ?? {}, loading: false })),
  on(programActions.getProgramRewardsFailure, (state): ProgramState => ({ ...state, rewardsList: state.rewardsList ?? {}, loading: false })),
  on(programActions.getEngagementsFailure, (state): ProgramState => ({ ...state, engagements: state.engagements ?? {}, loading: false })),

  on(programActions.getProgramCampaignsFailure, (state): ProgramState => ({
    ...state,
    campaigns: state.campaigns ?? {},
    loading: false,
    loadingCampaigns: false,
  })),

  on(programActions.getProgramTiersFailure, (state): ProgramState => ({ ...state, loading: false, loadingTiers: false })),

  on(
    programActions.getProgramLanguages,
    (state): ProgramState => ({ ...state, loadingTranslations: true }),
  ),

  on(
    programActions.getProgramLanguagesSuccess,
    (state, { programLanguages }): ProgramState => ({ ...state, programLanguages, loadingTranslations: false }),
  ),

  on(
    programActions.getProgramLanguagesFailure,
    (state): ProgramState => ({ ...state, loadingTranslations: false }),
  ),

  on(
    programActions.setProgramLanguagesSuccess,
    (state, { programLanguages }): ProgramState => ({ ...state, programLanguages }),
  ),

  on(programActions.getTemplateMetadata, (state): ProgramState => ({ ...state, templateMetadataFailed: false })),

  on(
    programActions.getTemplateMetadataSuccess,
    (state, { templateMetadata }): ProgramState => ({ ...state, templateMetadata, templateMetadataFailed: false }),
  ),

  on(programActions.getTemplateMetadataFailure, (state): ProgramState => ({ ...state, templateMetadataFailed: true })),

  on(
    programActions.getSectionTranslationsSuccess,
    (state, { locale, translations, entityType = "section" }): ProgramState => {
      const filtered = state.sectionTranslations.filter(t => t.locale !== locale || t.entityType !== entityType);
      return { ...state, sectionTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertSectionTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.sectionTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, sectionTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateSections,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateSectionsSuccess,
    programActions.autoTranslateSectionsFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getEarningFlowTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.earningFlowTranslations.filter(t => t.locale !== locale);
      return { ...state, earningFlowTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertEarningFlowTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.earningFlowTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, earningFlowTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateEarningFlows,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateEarningFlowsSuccess,
    programActions.autoTranslateEarningFlowsFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getProfileFormTranslationsSuccess,
    (state, { locale, translations }): ProgramState => ({
      ...state,
      profileFormTranslations: [...state.profileFormTranslations.filter(t => t.locale !== locale), ...translations],
    }),
  ),

  on(
    programActions.upsertProfileFormTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.profileFormTranslations];
      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) updated[idx] = t;
        else updated.push(t);
      }
      return { ...state, profileFormTranslations: updated };
    },
  ),

  on(
    programActions.upsertProfileFormTranslationsFailure,
    (state, { error }): ProgramState => ({ ...state, formError: error, loading: false }),
  ),

  on(programActions.autoTranslateProfileForm, (state): ProgramState => ({ ...state, autoTranslating: true })),
  on(
    programActions.autoTranslateProfileFormSuccess,
    programActions.autoTranslateProfileFormFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getCustomerMetafieldDefinitions,
    (state, { programId }): ProgramState => ({
      ...state,
      loadingCustomerMetafieldDefinitions: true,
      customerMetafieldDefinitionsError: null,
      // Another program's list is dropped while this one loads
      customerMetafieldDefinitions: programId === state.customerMetafieldDefinitionsProgramId ? state.customerMetafieldDefinitions : null,
      customerMetafieldDefinitionsProgramId: programId,
    }),
  ),
  on(
    programActions.getCustomerMetafieldDefinitionsSuccess,
    (state, { definitions }): ProgramState => ({ ...state, customerMetafieldDefinitions: definitions, loadingCustomerMetafieldDefinitions: false }),
  ),
  on(
    programActions.getCustomerMetafieldDefinitionsFailure,
    (state, { error }): ProgramState => ({ ...state, loadingCustomerMetafieldDefinitions: false, customerMetafieldDefinitionsError: error }),
  ),
  on(
    programActions.createCustomerMetafieldDefinition,
    (state): ProgramState => ({ ...state, creatingCustomerMetafieldDefinition: true, createCustomerMetafieldDefinitionError: null }),
  ),
  on(
    programActions.createCustomerMetafieldDefinitionSuccess,
    (state, { definition }): ProgramState => ({
      ...state,
      creatingCustomerMetafieldDefinition: false,
      customerMetafieldDefinitions: [...(state.customerMetafieldDefinitions ?? []).filter(({ id }) => id !== definition.id), definition],
    }),
  ),
  on(
    programActions.createCustomerMetafieldDefinitionFailure,
    (state, { error }): ProgramState => ({ ...state, creatingCustomerMetafieldDefinition: false, createCustomerMetafieldDefinitionError: error }),
  ),

  // The field a rejected custom profile form points at; the banner shows the reason (formError)
  on(
    programActions.createEngagementFailure,
    programActions.updateEngagementFailure,
    (state, { profileFormError }): ProgramState => ({ ...state, profileFormSaveError: profileFormError ?? null }),
  ),

  on(
    programActions.getRewardTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.rewardTranslations.filter(t => t.locale !== locale);
      return { ...state, rewardTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertRewardTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.rewardTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, rewardTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateRewards,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateRewardsSuccess,
    programActions.autoTranslateRewardsFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getTierTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.tierTranslations.filter(t => t.locale !== locale);
      return { ...state, tierTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertTierTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.tierTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, tierTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateTiers,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateTiersSuccess,
    programActions.autoTranslateTiersFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getCouponTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.couponTranslations.filter(t => t.locale !== locale);
      return { ...state, couponTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertCouponTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.couponTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, couponTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateCoupons,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateCouponsSuccess,
    programActions.autoTranslateCouponsFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getCampaignTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.campaignTranslations.filter(t => t.locale !== locale);
      return { ...state, campaignTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertCampaignTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.campaignTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.entityType === t.entityType && existing.entityId === t.entityId && existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, campaignTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateCampaigns,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateCampaignsSuccess,
    programActions.autoTranslateCampaignsFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  on(
    programActions.getProgramTranslationsSuccess,
    (state, { locale, translations }): ProgramState => {
      const filtered = state.programTranslations.filter(t => t.locale !== locale);
      return { ...state, programTranslations: [...filtered, ...translations] };
    },
  ),

  on(
    programActions.upsertProgramTranslationsSuccess,
    (state, { translations }): ProgramState => {
      const updated = [...state.programTranslations];

      for (const t of translations) {
        const idx = updated.findIndex(
          existing => existing.field === t.field && existing.locale === t.locale,
        );
        if (idx >= 0) {
          updated[idx] = t;
        } else {
          updated.push(t);
        }
      }
      return { ...state, programTranslations: updated };
    },
  ),

  on(
    programActions.autoTranslateProgram,
    (state): ProgramState => ({ ...state, autoTranslating: true }),
  ),

  on(
    programActions.autoTranslateProgramSuccess,
    programActions.autoTranslateProgramFailure,
    (state): ProgramState => ({ ...state, autoTranslating: false }),
  ),

  // An editor opened by URL leaves its skeleton when its entity cannot be loaded; a new load clears the error
  on(programActions.getCampaignById, programActions.getCampaignByIdSuccess, (state, action): ProgramState => {
    const campaignId = "campaign" in action ? action.campaign.id : action.campaignId;
    const { [campaignId]: _cleared, ...campaignLoadErrors } = state.campaignLoadErrors;
    return { ...state, campaignLoadErrors };
  }),

  on(
    programActions.getCampaignByIdFailure,
    (state, { campaignId, status }): ProgramState => ({
      ...state,
      campaignLoadErrors: { ...state.campaignLoadErrors, [campaignId]: status === 404 ? "notFound" : "failed" },
    }),
  ),

  on(programActions.getBoosts, programActions.getBoostsSuccess, (state): ProgramState => ({ ...state, boostsLoadFailed: false })),

  on(programActions.getBoostsFailure, (state): ProgramState => ({ ...state, boostsLoadFailed: true })),
);
